/**
 * Normalized classification for fetch failures across browsers.
 *
 * Why this exists:
 * - Browser fetch rejections are inconsistent (messages differ by engine).
 * - We want a small, stable set of buckets for logging and handling.
 */
export type FetchFailure =
  | {
      type: 'http'
      message: string
      url: string
      method: string
      status: number
      ok: false
    }
  | {
      type: 'abort'
      message: string
      url: string
      method: string
      name?: string
      signalAborted: boolean
    }
  | {
      type: 'timeout'
      message: string
      url: string
      method: string
      timeoutMs?: number
    }
  | {
      type: 'network'
      message: string
      url: string
      method: string
      name?: string
      isWebKitLoadFailed?: boolean
      isFailedToFetch?: boolean
      isFirefoxNetworkError?: boolean
      cause?: unknown
    }
  | {
      type: 'invalidRequest'
      message: string
      url: string
      method: string
      name?: string
      cause?: unknown
    }
  | {
      type: 'unknown'
      message: string
      url: string
      method: string
      cause?: unknown
    }

/**
 * Minimal request trace for error reporting.
 */
export type RequestTrace = {
  url: string
  method: string
  headers?: Record<string, string>
  body?: unknown
}

/**
 * Minimal response trace for non-success HTTP failures.
 */
export type ResponseTrace = {
  status: number
  headers?: Record<string, string>
  body?: unknown
}

export type InvalidRequestReason =
  | 'invalid_url'
  | 'encode_error'
  | 'headers_error'
  | 'unknown'

export type InvalidResponseReason =
  | 'invalid_json'
  | 'schema_mismatch'
  | 'read_error'
  | 'unknown'

export class TransportError extends Error {
  readonly _tag = 'TransportError' as const
  readonly cause?: unknown
  readonly description?: string
  readonly classification?: FetchFailure

  constructor(
    public request: RequestTrace,
    opts?: {
      cause?: unknown
      description?: string
      classification?: FetchFailure
    }
  ) {
    super(`Transport request failed: ${request.method} ${request.url}`)
    this.name = 'TransportError'
    this.cause = opts?.cause
    this.description = opts?.description
    this.classification = opts?.classification
    Object.setPrototypeOf(this, TransportError.prototype)
  }
}

export class TimeoutError extends Error {
  readonly _tag = 'TimeoutError' as const
  readonly cause?: unknown
  readonly description?: string
  readonly timeoutMs?: number

  constructor(
    public request: RequestTrace,
    opts?: {
      cause?: unknown
      description?: string
      timeoutMs?: number
    }
  ) {
    super(`Request timed out: ${request.method} ${request.url}`)
    this.name = 'TimeoutError'
    this.cause = opts?.cause
    this.description = opts?.description
    this.timeoutMs = opts?.timeoutMs
    Object.setPrototypeOf(this, TimeoutError.prototype)
  }
}

export class HttpStatusError extends Error {
  readonly _tag = 'HttpStatusError' as const
  readonly cause?: unknown
  readonly description?: string

  constructor(
    public request: RequestTrace,
    public response: ResponseTrace,
    opts?: {
      cause?: unknown
      description?: string
    }
  ) {
    super(`HTTP request failed: ${request.method} ${request.url} (${response.status})`)
    this.name = 'HttpStatusError'
    this.cause = opts?.cause
    this.description = opts?.description
    Object.setPrototypeOf(this, HttpStatusError.prototype)
  }
}

export class InvalidRequestError extends Error {
  readonly _tag = 'InvalidRequestError' as const
  readonly cause?: unknown
  readonly description?: string

  constructor(
    public request: RequestTrace,
    public reason: InvalidRequestReason,
    opts?: {
      cause?: unknown
      description?: string
    }
  ) {
    super(`Invalid request: ${request.method} ${request.url} (${reason})`)
    this.name = 'InvalidRequestError'
    this.cause = opts?.cause
    this.description = opts?.description
    Object.setPrototypeOf(this, InvalidRequestError.prototype)
  }
}

export class InvalidResponseError extends Error {
  readonly _tag = 'InvalidResponseError' as const
  readonly cause?: unknown
  readonly description?: string

  constructor(
    public request: RequestTrace,
    public reason: InvalidResponseReason,
    opts?: {
      response?: ResponseTrace
      cause?: unknown
      description?: string
    }
  ) {
    super(`Invalid response: ${request.method} ${request.url} (${reason})`)
    this.name = 'InvalidResponseError'
    this.response = opts?.response
    this.cause = opts?.cause
    this.description = opts?.description
    Object.setPrototypeOf(this, InvalidResponseError.prototype)
  }

  readonly response?: ResponseTrace
}

export type CommandError =
  | TransportError
  | TimeoutError
  | HttpStatusError
  | InvalidRequestError
  | InvalidResponseError

export function isTransportError(error: unknown): error is TransportError {
  return error instanceof TransportError || hasTag(error, 'TransportError')
}

export function isTimeoutError(error: unknown): error is TimeoutError {
  return error instanceof TimeoutError || hasTag(error, 'TimeoutError')
}

export function isHttpStatusError(error: unknown): error is HttpStatusError {
  return error instanceof HttpStatusError || hasTag(error, 'HttpStatusError')
}

export function isInvalidRequestError(error: unknown): error is InvalidRequestError {
  return error instanceof InvalidRequestError || hasTag(error, 'InvalidRequestError')
}

export function isInvalidResponseError(error: unknown): error is InvalidResponseError {
  return error instanceof InvalidResponseError || hasTag(error, 'InvalidResponseError')
}

export function isCommandError(error: unknown): error is CommandError {
  return (
    isTransportError(error) ||
    isTimeoutError(error) ||
    isHttpStatusError(error) ||
    isInvalidRequestError(error) ||
    isInvalidResponseError(error)
  )
}

function hasTag(error: unknown, tag: CommandError['_tag']): boolean {
  return (
    typeof error == 'object' &&
    error != null &&
    '_tag' in error &&
    (error as { _tag: unknown })._tag == tag
  )
}

type InferFetchFailureParams = {
  request: { url: string; method: string }
  response?: Response
  error?: unknown
  signal?: AbortSignal | null
  timeoutMs?: number | null
}

/**
 * Classify a fetch failure using request-ish inputs and the thrown error.
 */
export function inferFetchFailure({
  request,
  response,
  error,
  signal,
  timeoutMs
}: InferFetchFailureParams): FetchFailure {
  if (response && !response.ok) {
    return {
      type: 'http',
      message: `HTTP ${response.status} for ${request.method} ${request.url}`,
      url: request.url,
      method: request.method,
      status: response.status,
      ok: false
    }
  }

  const rootError = unwrapUnknownException(error)
  const message = rootError instanceof Error ? rootError.message : 'unknown'
  const name = rootError instanceof Error ? rootError.name : undefined

  if (isAbortError({ error: rootError, signal })) {
    return {
      type: 'abort',
      message,
      url: request.url,
      method: request.method,
      name,
      signalAborted: Boolean(signal?.aborted)
    }
  }

  if (typeof timeoutMs == 'number' && timeoutMs > 0) {
    return {
      type: 'timeout',
      message,
      url: request.url,
      method: request.method,
      timeoutMs
    }
  }

  if (isTimeoutLikeError(rootError)) {
    return {
      type: 'timeout',
      message,
      url: request.url,
      method: request.method,
      timeoutMs: timeoutMs ?? undefined
    }
  }

  if (isInvalidRequestErrorMessage(message)) {
    return {
      type: 'invalidRequest',
      message,
      url: request.url,
      method: request.method,
      name,
      cause: rootError
    }
  }

  if (rootError instanceof TypeError) {
    return {
      type: 'network',
      message,
      url: request.url,
      method: request.method,
      name,
      isWebKitLoadFailed: isWebKitLoadFailedMessage(message),
      isFailedToFetch: isFailedToFetchMessage(message),
      isFirefoxNetworkError: isFirefoxNetworkErrorMessage(message),
      cause: rootError
    }
  }

  return {
    type: 'unknown',
    message,
    url: request.url,
    method: request.method,
    cause: rootError
  }
}

function unwrapUnknownException(error: unknown): unknown {
  if (typeof error != 'object' || error == null) {
    return error
  }

  const unknownException = error as { _tag?: unknown; error?: unknown; cause?: unknown }
  if (unknownException._tag != 'UnknownException') {
    return error
  }

  if (unknownException.error != null && unknownException.error != error) {
    return unwrapUnknownException(unknownException.error)
  }

  if (unknownException.cause != null && unknownException.cause != error) {
    return unwrapUnknownException(unknownException.cause)
  }

  return error
}

function isAbortError(params: { error?: unknown; signal?: AbortSignal | null }): boolean {
  if (params.signal?.aborted) {
    return true
  }
  if (params.error instanceof Error) {
    return params.error.name == 'AbortError'
  }
  return false
}

function isTimeoutLikeError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false
  }

  if (error.name == 'TimeoutError') {
    return true
  }

  const message = error.message.toLowerCase()
  return message.includes('timeout') || message.includes('timed out')
}

function isWebKitLoadFailedMessage(message: string): boolean {
  return message == 'Load failed'
}

function isFailedToFetchMessage(message: string): boolean {
  return message == 'Failed to fetch'
}

function isFirefoxNetworkErrorMessage(message: string): boolean {
  return message == 'NetworkError when attempting to fetch resource.'
}

function isInvalidRequestErrorMessage(message: string): boolean {
  return (
    message.includes('Failed to construct') ||
    message.includes('Invalid URL') ||
    message.includes('Request constructor')
  )
}
